import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, Chip, CircularProgress, Button,
  alpha, Stack, Alert, Divider, Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getHouseQuestion } from '../services/api';

const THEME_COLOR = '#00897b';

const CHAMBER_DISPLAY = {
  house: 'House of Representatives',
  federation: 'Federation Chamber',
};

// One MUI Chip colour per turn role. "info"/"success"/"default" are visually
// distinguishable in both light + dark mode and don't fight the page theme.
const ROLE_CHIP = {
  question: { color: 'info', label: 'Question' },
  answer: { color: 'success', label: 'Answer' },
  interjection: { color: 'default', label: 'Interjection' },
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function formatStartTime(iso) {
  if (!iso) return null;
  // ES holds AEST naive ISO strings (e.g. 2026-05-25T14:00:42). Pluck the
  // clock time directly from the string to avoid timezone shifting.
  const m = /T(\d{2}:\d{2}(?::\d{2})?)/.exec(iso);
  return m ? m[1] : null;
}

export default function HouseQuestionsDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Full speaker-turn transcript and the plain merged transcript are both
  // collapsed by default — the page's headline value is the clean
  // Question / Answer split above.
  const [turnsOpen, setTurnsOpen] = useState(false);
  const [plainOpen, setPlainOpen] = useState(false);

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getHouseQuestion(id);
        setDoc(data);
      } catch (err) {
        console.error('Error fetching house question:', err);
        setError(err.response?.status === 404 ? 'Question not found' : 'Failed to load question');
      }
      setLoading(false);
    };
    fetchDoc();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: THEME_COLOR }} />
      </Box>
    );
  }

  if (error || !doc) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error || 'Question not found'}</Typography>
        <Button component={RouterLink} to="/house-questions" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }}>
          Back to House Questions
        </Button>
      </Box>
    );
  }

  const chamberDisplay = CHAMBER_DISPLAY[doc.chamber] || doc.chamber;
  const dateLong = formatDate(doc.date);
  const startTime = formatStartTime(doc.start_time);
  const askerName = doc.asker?.name || 'Unknown';
  const responderName = doc.responder?.name || 'Unknown';
  const portfolioName = doc.responder?.portfolio;
  const askerElectorate = doc.asker?.electorate;
  const responderElectorate = doc.responder?.electorate;
  const speakerTurns = Array.isArray(doc.speaker_turns) ? doc.speaker_turns : [];
  const hasTurns = speakerTurns.length > 0;

  // Defensive fallbacks: 79/79 docs have these today, but the scraper is
  // best-effort so future days may produce a doc where the heuristic couldn't
  // recover one or the other. When that happens, fall back to the raw_line
  // (for the question — the asker→responder header line) or the plain
  // transcript (for the answer — the only thing we have left).
  const questionText = doc.question_text || null;
  const answerText = doc.answer_text || null;

  // Same-search backlinks — point at the search page with the relevant facet
  // already applied. encodeURIComponent so names with punctuation survive.
  const askerLink = `/house-questions?asker=${encodeURIComponent(askerName)}`;
  const responderLink = `/house-questions?responder=${encodeURIComponent(responderName)}`;
  const portfolioLink = portfolioName
    ? `/house-questions?portfolio=${encodeURIComponent(portfolioName)}`
    : null;
  const dayLink = doc.date
    ? `/house-questions?chamber=${encodeURIComponent(doc.chamber || '')}`
    : null;

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to={`/house-questions${doc.chamber ? `?chamber=${encodeURIComponent(doc.chamber)}` : ''}`}
          startIcon={<ArrowBackIcon />}
          sx={{ color: 'text.secondary' }}
        >
          Back to House Questions
        </Button>
      </Box>

      {/* Hero */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(THEME_COLOR, 0.08)} 0%, ${alpha(THEME_COLOR, 0.02)} 100%)`,
          border: '1px solid',
          borderColor: alpha(THEME_COLOR, 0.15),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <QuestionAnswerIcon sx={{ fontSize: 48, color: THEME_COLOR }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: THEME_COLOR, mb: 0.5 }}>
              Question {doc.question_number ?? '?'}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {chamberDisplay} {dateLong ? `— ${dateLong}` : ''}
            </Typography>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {startTime && (
                <Chip icon={<AccessTimeIcon />} label={startTime} size="small" sx={{ bgcolor: THEME_COLOR, color: 'white', '& .MuiChip-icon': { color: 'white' } }} />
              )}
              {dateLong && (
                <Chip icon={<CalendarMonthIcon />} label={dateLong} size="small" variant="outlined" />
              )}
              {doc.transcript_segment_count != null && (
                <Chip label={`${doc.transcript_segment_count} caption segment${doc.transcript_segment_count === 1 ? '' : 's'}`} size="small" variant="outlined" />
              )}
              {hasTurns && (
                <Chip label={`${speakerTurns.length} speaker turn${speakerTurns.length === 1 ? '' : 's'}`} size="small" variant="outlined" />
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip
                icon={<PersonIcon />}
                label={`Asker: ${askerName}${askerElectorate ? ` (${askerElectorate})` : ''}`}
                size="small"
                component={RouterLink}
                to={askerLink}
                clickable
                variant="outlined"
              />
              <Chip
                icon={<RecordVoiceOverIcon />}
                label={`Responder: ${responderName}${responderElectorate ? ` (${responderElectorate})` : ''}`}
                size="small"
                component={RouterLink}
                to={responderLink}
                clickable
                variant="outlined"
              />
              {portfolioName && portfolioLink && (
                <Chip
                  icon={<WorkOutlineIcon />}
                  label={`Portfolio: ${portfolioName}`}
                  size="small"
                  component={RouterLink}
                  to={portfolioLink}
                  clickable
                  variant="outlined"
                />
              )}
              {dayLink && (
                <Chip
                  label={`All ${chamberDisplay} QT`}
                  size="small"
                  component={RouterLink}
                  to={dayLink}
                  clickable
                  variant="outlined"
                />
              )}
            </Stack>

            {doc.raw_line && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2 }}>
                {doc.raw_line}
              </Typography>
            )}

            {doc.source_url && (
              <Button
                size="small"
                variant="outlined"
                href={doc.source_url}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon />}
                sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
              >
                View on HouseLive
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* DRAFT/captions caveat */}
      <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        <strong>Live captions — not the official record.</strong> This transcript was produced automatically from the HouseLive caption stream and may contain mis-hearings, missing speaker turns, or interleaved Speaker interjections. The authoritative record is Hansard.
      </Alert>

      {/* Question card — first prominent section. */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha('#1976d2', 0.4),
          background: alpha('#1976d2', 0.04),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <HelpOutlineIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
            Question
          </Typography>
          <Chip
            label={askerName}
            size="small"
            component={RouterLink}
            to={askerLink}
            clickable
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Box>
        {questionText ? (
          <Typography variant="body1" sx={{ lineHeight: 1.7, fontSize: '1.05rem', whiteSpace: 'pre-wrap' }}>
            {questionText}
          </Typography>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
              Couldn't isolate the question from the captions for this entry. Showing the raw header line; see Full transcript below for the source caption turns.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, fontStyle: 'italic' }}>
              {doc.raw_line || '—'}
            </Typography>
          </>
        )}
      </Paper>

      {/* Answer card — second prominent section. */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: alpha('#2e7d32', 0.4),
          background: alpha('#2e7d32', 0.04),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32' }}>
            Answer
          </Typography>
          <Chip
            label={`${responderName}${portfolioName ? ` — ${portfolioName}` : ''}`}
            size="small"
            component={RouterLink}
            to={responderLink}
            clickable
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Box>
        {answerText ? (
          <Typography variant="body1" sx={{ lineHeight: 1.7, fontSize: '1.05rem', whiteSpace: 'pre-wrap' }}>
            {answerText}
          </Typography>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
              Couldn't isolate the answer from the captions for this entry. See Full transcript below for the source caption turns.
            </Typography>
            <Typography variant="body1" sx={{ lineHeight: 1.7, fontStyle: 'italic' }}>
              {doc.transcript ? doc.transcript : '—'}
            </Typography>
          </>
        )}
      </Paper>

      {/* Caveat: the Q/A split is heuristic — point readers at the source-of-
          truth turn structure below. */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3, px: 1 }}>
        Question and answer text are derived from live captions; caption colours don't uniquely identify speakers across the day, so the split is heuristic — see Full transcript below for the source structure.
      </Typography>

      {/* Expandable full transcript — speaker_turns is the source of truth. */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 2 }}>
        <Button
          fullWidth
          onClick={() => setTurnsOpen((v) => !v)}
          endIcon={turnsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            justifyContent: 'space-between',
            textTransform: 'none',
            color: 'text.primary',
            py: 1.5,
            px: 2.5,
            borderRadius: 3,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Full transcript with chair interjections
            {hasTurns ? ` (${speakerTurns.length} turns)` : ''}
          </Typography>
        </Button>
        <Collapse in={turnsOpen} unmountOnExit>
          <Box sx={{ p: 2.5, pt: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Source-of-truth caption turns in chronological order. The role chip (Question / Answer / Interjection) is the scraper's classification of each turn; the colour value is the underlying VTT caption colour (often null — diagnostic only).
            </Typography>

            {hasTurns ? (
              <Stack spacing={1.5}>
                {speakerTurns.map((turn, idx) => {
                  const role = ROLE_CHIP[turn.role] || ROLE_CHIP.interjection;
                  return (
                    <Paper
                      key={idx}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Chip label={role.label} color={role.color} size="small" />
                        <Typography variant="caption" color="text.secondary">
                          #{idx + 1}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          colour: <code>{turn.color == null ? 'null' : String(turn.color)}</code>
                        </Typography>
                        {turn.chars != null && (
                          <Typography variant="caption" color="text.secondary">
                            {turn.chars} char{turn.chars === 1 ? '' : 's'}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {turn.text}
                      </Typography>
                    </Paper>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No structured speaker turns captured for this question.
              </Typography>
            )}

            {/* Inline secondary toggle for the plain merged transcript —
                handy for copy/paste; kept nested here rather than as a
                separate top-level card so the page doesn't grow another
                long blob by default. */}
            {doc.transcript && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ mb: 2 }} />
                <Button
                  size="small"
                  onClick={() => setPlainOpen((v) => !v)}
                  endIcon={plainOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  {plainOpen ? 'Hide plain transcript' : 'Show plain transcript (merged, no roles)'}
                </Button>
                <Collapse in={plainOpen} unmountOnExit>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      mt: 1.5,
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.7,
                      color: 'text.secondary',
                    }}
                  >
                    {doc.transcript}
                  </Typography>
                </Collapse>
              </Box>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* Same-search backlinks. */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            component={RouterLink}
            to={askerLink}
            size="small"
            variant="outlined"
            sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
          >
            More questions from {askerName}
          </Button>
          <Button
            component={RouterLink}
            to={responderLink}
            size="small"
            variant="outlined"
            sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
          >
            More to {responderName}
          </Button>
          {portfolioLink && (
            <Button
              component={RouterLink}
              to={portfolioLink}
              size="small"
              variant="outlined"
              sx={{ borderColor: THEME_COLOR, color: THEME_COLOR }}
            >
              More in {portfolioName}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
